import {createContext, useContext} from 'react';

import type {StaffViewAs, StaffViewAsKind, StaffViewAsSeat} from './staffViewAs';

/**
 * Staff viewing the course as a made-up student. `ProfileProvider` holds the
 * state and provides this context, because the view changes the profile it
 * provides.
 */
export interface StaffViewAsContextValue {
  /** The view for the current course, or `null` for the viewer's own. */
  viewAs: StaffViewAs | null;
  /**
   * Whether the viewer is course staff (TA and above) by their real role,
   * which a view does not change.
   */
  isStaff: boolean;
  /**
   * Starts viewing the current course as a new made-up student, or stops
   * with `null`. `onboardingData` seeds the student's onboarding doc;
   * omitted means an empty one. `seat` is the student's small section;
   * omitted means none. The catch-up page's week carries over.
   */
  setViewAsKind: (
    kind: StaffViewAsKind | null,
    onboardingData?: Record<string, unknown>,
    seat?: StaffViewAsSeat,
  ) => void;
  /**
   * Sets the section week (one-based) the catch-up page shows during a
   * view, or `null` for the weeks that have met. No-op without a view.
   */
  setViewAsSectionWeek: (sectionWeek: number | null) => void;
  /** Merges fields into the made-up student's onboarding doc. */
  editViewAsOnboarding: (fields: Record<string, unknown>) => void;
}

/** {@link StaffViewAsContextValue}; outside a `ProfileProvider`, no view. */
export const StaffViewAsContext = createContext<StaffViewAsContextValue>({
  viewAs: null,
  isStaff: false,
  setViewAsKind: () => {},
  setViewAsSectionWeek: () => {},
  editViewAsOnboarding: () => {},
});

/**
 * Staff viewing the course as a made-up student.
 *
 * @return The context value.
 * @throws Never.
 */
export function useStaffViewAs(): StaffViewAsContextValue {
  return useContext(StaffViewAsContext);
}
